import type { Report } from "../../lib/api/index.js";
import { PATHS } from "../../lib/routes.js";
import { navigate, withReport } from "../../lib/url-filters.js";
import { AppLink } from "../app-link.js";
import { BrandMark } from "../brand-mark.js";
import { ChevronsUpDown, Pause, Plus } from "../icons.js";
import { useMenu } from "../use-menu.js";
import { ReportState } from "./report-state.js";

/** The screens about one brand. Switching brand on one of them stays on it. */
const BRAND_PATHS: readonly string[] = [
  PATHS.overview,
  PATHS.prompts,
  PATHS.rivals,
  PATHS.citations,
  PATHS.offPage,
  PATHS.settings,
];

/**
 * The brand on screen, at the head of its box in the rail, and the way to
 * every other brand (DESIGN.md §5, "The picker" and "The brand menu").
 *
 * Brands only. The workspaces were in this menu too until 2026-10-13, which
 * made choosing a brand look like changing workspace; they have their own
 * button above the box now.
 */
export function BrandSwitch({
  reports,
  current,
  path,
  canAddBrands,
  workspaceId,
}: {
  /** In the workspace's own order, which the menu keeps. */
  readonly reports: readonly Report[];
  readonly current: Report | undefined;
  /** The address on screen, so choosing a brand can stay on the same screen. */
  readonly path: string;
  readonly canAddBrands: boolean;
  /** Whose reports these are, for reading each one's capture state. */
  readonly workspaceId: string;
}) {
  const menu = useMenu();
  // The same screen for another brand, or that brand's Brief from anywhere else.
  const screen = BRAND_PATHS.includes(path) ? path : PATHS.overview;

  return (
    <div className="side-switch-wrap" ref={menu.root} onKeyDown={menu.onKeyDown}>
      <button
        type="button"
        className="side-switch"
        aria-expanded={menu.open}
        aria-controls="brand-menu"
        onClick={menu.toggle}
      >
        {current === undefined ? null : (
          <BrandMark
            name={current.brand.name}
            domain={current.brand.primaryDomain}
            size="sm"
          />
        )}
        <span className="side-switch-text">
          <span className="side-switch-name">
            {current === undefined ? "No brand yet" : current.brand.name}
            {current?.paused === true ? (
              <>
                <Pause className="side-switch-paused" aria-hidden="true" />
                <span className="visually-hidden"> (paused)</span>
              </>
            ) : null}
          </span>
        </span>
        <ChevronsUpDown className="side-switch-icon" aria-hidden="true" />
        <span className="visually-hidden">, switch brand</span>
      </button>

      {menu.open ? (
        <div className="account-menu side-menu" id="brand-menu">
          <p className="side-menu-label">Brands in this workspace</p>
          {reports.map((report) => (
            <AppLink
              key={report.id}
              href={withReport(screen, report.id)}
              className="account-menu-item side-menu-brand"
              aria-current={report.id === current?.id ? "page" : undefined}
              onNavigate={menu.close}
            >
              <BrandMark
                name={report.brand.name}
                domain={report.brand.primaryDomain}
                size="xs"
              />
              <span className="side-menu-name">{report.brand.name}</span>
              <ReportState
                workspaceId={workspaceId}
                report={report}
                isCurrent={report.id === current?.id}
              />
            </AppLink>
          ))}
          {canAddBrands ? (
            <button
              type="button"
              className="account-menu-item"
              onClick={() => {
                // Another company, measured on its own: the same three steps
                // the first one took. The current brand rides along so Cancel
                // can come back to it.
                menu.close();
                navigate(
                  current === undefined
                    ? PATHS.newBrand
                    : withReport(PATHS.newBrand, current.id),
                );
              }}
            >
              <Plus className="account-menu-icon" aria-hidden="true" />
              Add a brand
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
